/**
 * Utility functions for transforming API line items to generateDocument format.
 *
 * The API returns line items with `tax_ids` (array of tax references).
 * The generateDocument function expects `tax_rate` and `tax_amount`.
 */

import type { LineItem as TemplateLineItem, LineItemSection } from '../types';
import type { LineItem as ApiLineItem, Tax } from '@/lib/api';

/**
 * Tax information can come in two formats:
 * - Array format (internal pages): Tax[]
 * - Object format (public pages): Record<string, { name: string; rate: string }>
 */
export type TaxLookup =
  | Tax[]
  | Record<string, { name: string; rate: string }>;

/**
 * Get tax rate from tax lookup by ID.
 * Returns the rate as a number (e.g., 10 for 10%).
 */
function getTaxRate(taxId: string, taxes: TaxLookup): number {
  if (Array.isArray(taxes)) {
    // Internal pages - taxes is Tax[]
    const tax = taxes.find((t) => t.id === taxId);
    return tax ? parseFloat(tax.rate) : 0;
  } else {
    // Public pages - taxes is Record<string, { name, rate }>
    const tax = taxes[taxId];
    return tax ? parseFloat(tax.rate) : 0;
  }
}

/**
 * Transform a single API line item to Template line item format.
 */
function transformItem(item: ApiLineItem, taxes: TaxLookup): TemplateLineItem {
  // Calculate combined tax rate from all tax_ids
  let combinedTaxRate = 0;
  if (item.tax_ids && item.tax_ids.length > 0) {
    item.tax_ids.forEach((taxId) => {
      combinedTaxRate += getTaxRate(taxId, taxes);
    });
  }

  // Use computed values (with markup applied) if available, otherwise fall back to raw values
  const effectiveRate = item.computed_rate ?? item.rate;
  const effectiveAmount = item.computed_amount ?? item.amount;

  // Calculate tax amount based on the effective (possibly marked-up) amount
  const taxAmount = effectiveAmount * (combinedTaxRate / 100);

  return {
    name: item.name,
    code: item.code || '',
    description: item.description || '',
    quantity: item.quantity,
    rate: effectiveRate,
    item_type: (item.item_type as 'product_parts' | 'labor' | 'other') || 'product_parts',
    discount: 0,
    discount_type: 'percentage',
    tax_rate: combinedTaxRate,
    tax_amount: taxAmount,
    amount: effectiveAmount,
  };
}

/**
 * Transform API line items (sectioned format) to generateDocument format.
 *
 * @param apiLineItems - Line items from API: [string, ApiLineItem[]][]
 * @param taxes - Tax lookup (array or object format)
 * @returns Transformed line items for generateDocument
 */
export function transformLineItems(
  apiLineItems: [string, ApiLineItem[]][],
  taxes: TaxLookup
): LineItemSection[] {
  return apiLineItems.map(([sectionName, items]) => [
    sectionName,
    items.map((item) => transformItem(item, taxes)),
  ]);
}

/**
 * Calculate total tax from API line items.
 * Uses computed_amount (with markup) if available.
 *
 * @param apiLineItems - Line items from API: [string, ApiLineItem[]][]
 * @param taxes - Tax lookup (array or object format)
 * @returns Total tax amount
 */
export function calculateTotalTax(
  apiLineItems: [string, ApiLineItem[]][],
  taxes: TaxLookup
): number {
  let totalTax = 0;

  apiLineItems.forEach(([, items]) => {
    items.forEach((item) => {
      if (item.tax_ids && item.tax_ids.length > 0) {
        const effectiveAmount = item.computed_amount ?? item.amount;
        item.tax_ids.forEach((taxId) => {
          const taxRate = getTaxRate(taxId, taxes) / 100;
          totalTax += effectiveAmount * taxRate;
        });
      }
    });
  });

  return totalTax;
}

/**
 * Calculate subtotal from API line items.
 * Uses computed_amount (with markup) if available.
 *
 * @param apiLineItems - Line items from API: [string, ApiLineItem[]][]
 * @returns Subtotal (sum of computed amounts)
 */
export function calculateSubtotal(
  apiLineItems: [string, ApiLineItem[]][]
): number {
  let subtotal = 0;

  apiLineItems.forEach(([, items]) => {
    items.forEach((item) => {
      subtotal += item.computed_amount ?? item.amount;
    });
  });

  return subtotal;
}
